import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { Alert, Button, Link, Typography } from '@mui/material';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { Loader } from 'src/components';
import { useClientSegmentDeleteFlow } from 'src/components/ClientSegmentModals/use-client-segment-delete-flow';
import { type BrandWithSupport, useBrandsWithSupport } from 'src/graphql/entities/brand';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { usePartners } from 'src/graphql/entities/partner/partner';
import { useGlobalStore } from 'src/store';
import { Modals, useShowModals } from 'src/store/modals';
import { ClientSegmentCard } from './client-segment-card';
import {
  AddSegmentCard,
  ClientSegmentsContainer,
  ClientSegmentsDescriptor,
  ClientSegmentsList,
  EmptyStateContainer,
  EmptyStateIconWrap,
} from './styled';

const DESCRIPTOR_TEXT =
  'Customer-facing identities for routing calls, emails, and conversations.';

const buildActivationFlowLink = (zendeskDomain: string | null | undefined): string | null =>
  zendeskDomain ? `https://myprovision.io/${zendeskDomain}` : null;

export const ClientSegmentsTab = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { groupRules } = useGroupRules();
  const canManage = !!groupRules[UserGroupRules.AccessToManageClientSegments];

  const { rawPartnerMap } = usePartners();

  const rawPartner = useMemo(
    () =>
      isNullish(selectedPartnerId) ? null : (rawPartnerMap.get(selectedPartnerId) ?? null),
    [rawPartnerMap, selectedPartnerId],
  );

  const activationFlowLink = rawPartner?.zendeskDomain
    ? buildActivationFlowLink(rawPartner.zendeskDomain)
    : null;

  const { brands, loading, error } = useBrandsWithSupport(selectedPartnerId);

  const [showAddSegmentModal] = useShowModals(Modals.ClientSegmentAddModal);
  const [showEditSegmentModal] = useShowModals(Modals.ClientSegmentEditModal);
  const { start: startDeleteFlow } = useClientSegmentDeleteFlow();

  const handleEdit = (segment: BrandWithSupport, sitesCount: number) => {
    showEditSegmentModal({ segment, sitesCount });
  };

  const handleDelete = (segment: BrandWithSupport, sitesCount: number) => {
    void startDeleteFlow({ segment, sitesCount });
  };

  const handleAdd = () => {
    if (isNullish(selectedPartnerId)) return;
    showAddSegmentModal({ partnerId: selectedPartnerId });
  };

  const headerBlock = (
    <>
      <ClientSegmentsDescriptor>{DESCRIPTOR_TEXT}</ClientSegmentsDescriptor>
      {activationFlowLink ? (
        <Typography variant="body2" color="text.secondary">
          Link to access activation flow:{' '}
          <Link
            href={activationFlowLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: (theme) => theme.palette.primary.main,
              overflowWrap: 'anywhere',
            }}
          >
            {activationFlowLink}
          </Link>
        </Typography>
      ) : null}
    </>
  );

  if (loading) {
    return (
      <ClientSegmentsContainer sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Loader />
      </ClientSegmentsContainer>
    );
  }

  if (error) {
    return (
      <ClientSegmentsContainer>
        <Alert severity="error">Unable to load client segments. Please try again.</Alert>
      </ClientSegmentsContainer>
    );
  }

  if (brands.length === 0) {
    return (
      <ClientSegmentsContainer>
        {headerBlock}
        <EmptyStateContainer>
          <EmptyStateIconWrap>
            <LayersOutlinedIcon />
          </EmptyStateIconWrap>
          <Typography variant="h6">No client segments yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '26.25rem' }}>
            Add a brand to give customers a consistent experience across calls, emails, and
            conversations.
          </Typography>
          {canManage ? (
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={isNullish(selectedPartnerId)}
              sx={{ mt: 1 }}
            >
              Add your first segment
            </Button>
          ) : null}
        </EmptyStateContainer>
      </ClientSegmentsContainer>
    );
  }

  return (
    <ClientSegmentsContainer>
      {headerBlock}
      <ClientSegmentsList>
        {brands.map((segment) => (
          <ClientSegmentCard
            key={segment.brandId}
            segment={segment}
            canManage={canManage}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
        {canManage ? (
          <AddSegmentCard onClick={handleAdd} disabled={isNullish(selectedPartnerId)}>
            + Add new client segment
          </AddSegmentCard>
        ) : null}
      </ClientSegmentsList>
    </ClientSegmentsContainer>
  );
};
